// Photos of workings on a written answer in a mock exam. While sitting it, each photo is shrunk on the
// phone and uploaded straight away into the student's own folder of the private "answer-photos"
// bucket; afterwards the student and their teachers see them through short-lived links.
import { useEffect, useRef, useState } from 'preact/hooks';
import { ANSWER_PHOTOS, photoUrls, uploadPhoto } from '../api.js';
import { compressPhoto } from '../photos.js';
import { Icon } from './icons.js';
import { Button } from './components.jsx';

export const MAX_ANSWER_PHOTOS = 5;

function usePhotoUrls(paths) {
  const [urls, setUrls] = useState({});
  const key = (paths || []).join('|');
  useEffect(() => {
    let live = true;
    const missing = (paths || []).filter((p) => !urls[p]);
    if (missing.length) photoUrls(missing, ANSWER_PHOTOS).then((u) => { if (live) setUrls((old) => ({ ...old, ...u })); });
    return () => { live = false; };
  }, [key]);
  return urls;
}

export function AnswerPhotoGrid({ paths, label = 'working' }) {
  const urls = usePhotoUrls(paths);
  if (!paths || !paths.length) return null;
  return (
    <ul class="photo-grid">
      {paths.map((path, i) => (
        <li key={path}>
          {urls[path]
            ? <a href={urls[path]} target="_blank" rel="noopener"><img src={urls[path]} alt={`Photo ${i + 1} of the ${label}, opens full size`} loading="lazy" /></a>
            : <span class="photo-missing">Photo {i + 1}</span>}
        </li>
      ))}
    </ul>
  );
}

// Previews of photos added on this phone, shown at once (kept across questions).
const shown = {};

// While answering: onAdd(path) after each upload, onRemove(path). `uploading` is how many photos for this
// answer are still on their way (kept by the test screen, so it survives moving between questions), and
// onUploading(+1 / -1) reports them.
export function AnswerPhotoPicker({ paths, onAdd, onRemove, disabled, id, uploading = 0, onUploading = () => {} }) {
  const input = useRef(null);
  const [err, setErr] = useState(null);
  const urls = usePhotoUrls(paths);
  const pick = async (e) => {
    const files = Array.from(e.currentTarget.files || []).filter((f) => /^image\//.test(f.type || 'image/'));
    e.currentTarget.value = '';
    setErr(null);
    const room = MAX_ANSWER_PHOTOS - paths.length - uploading;
    if (files.length > room) setErr(`Up to ${MAX_ANSWER_PHOTOS} photos for each answer.`);
    const take = files.slice(0, Math.max(0, room));
    onUploading(take.length);
    let left = take.length;
    try {
      for (const f of take) {
        const blob = await compressPhoto(f);
        const path = await uploadPhoto(blob, ANSWER_PHOTOS);
        shown[path] = URL.createObjectURL(blob);
        onAdd(path);
        left -= 1;
        onUploading(-1);
      }
    } catch (ex) {
      setErr(ex.message || 'The photo didn’t upload. Please try again.');
    }
    if (left) onUploading(-left);
  };
  const busy = uploading > 0;
  return (
    <div class="photo-picker answer-photos">
      {paths.length ? (
        <ul class="photo-strip">
          {paths.map((p, i) => (
            <li key={p}>
              {shown[p] || urls[p] ? <img src={shown[p] || urls[p]} alt={`Photo ${i + 1} of your working`} /> : <span class="photo-missing">Photo {i + 1}</span>}
              {!disabled ? <button type="button" class="photo-remove" onClick={() => onRemove(p)} aria-label={`Remove photo ${i + 1}`}><Icon name="close" /></button> : null}
            </li>
          ))}
        </ul>
      ) : null}
      {!disabled && (paths.length + uploading < MAX_ANSWER_PHOTOS || busy) ? (
        <>
          <input ref={input} id={id} hidden type="file" accept="image/*" multiple onChange={pick} />
          <Button variant="line" size="sm" icon="camera" busy={busy} onClick={() => input.current && input.current.click()}>
            {busy ? 'Uploading the photo…' : paths.length ? 'Add another photo' : 'Add a photo of your working'}
          </Button>
        </>
      ) : null}
      {err ? <p class="field-error" role="alert">{err}</p> : null}
    </div>
  );
}
